import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiCall } from '../../utils/Apicalls';
import Header from "../../components/common/header/Header";
import Footer from "../../components/common/footer/Footer";
import SpinnerShow from '../../components/Spinner';
import { CheckCircle } from '@mui/icons-material';
import CurrencyRupee from '@mui/icons-material/CurrencyRupee';
import EuroSymbol from '@mui/icons-material/EuroSymbol';
import CurrencyPound from '@mui/icons-material/CurrencyPound';
import CurrencyYen from '@mui/icons-material/CurrencyYen';
import AttachMoney from '@mui/icons-material/AttachMoney';
import './PaymentResult.css';

function PaymentSuccessPage() {
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Function to get currency symbol based on country code
  const getCurrencySymbol = (countryCode) => {
    // Map of country codes to currency symbols
    const currencyMap = {
      'IN': { symbol: <CurrencyRupee fontSize="small" />, code: 'INR' },
      'GB': { symbol: <CurrencyPound fontSize="small" />, code: 'GBP' },
      'UK': { symbol: <CurrencyPound fontSize="small" />, code: 'GBP' },
      'EU': { symbol: <EuroSymbol fontSize="small" />, code: 'EUR' },
      'DE': { symbol: <EuroSymbol fontSize="small" />, code: 'EUR' },
      'FR': { symbol: <EuroSymbol fontSize="small" />, code: 'EUR' },
      'IT': { symbol: <EuroSymbol fontSize="small" />, code: 'EUR' },
      'ES': { symbol: <EuroSymbol fontSize="small" />, code: 'EUR' },
      'JP': { symbol: <CurrencyYen fontSize="small" />, code: 'JPY' },
      // Default to USD for all other countries
      'US': { symbol: <AttachMoney fontSize="small" />, code: 'USD' },
    };
    
    return currencyMap[countryCode] || currencyMap['US'];
  };
  
  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get purchase ID from localStorage
        const purchaseId = localStorage.getItem('currentPurchaseId');
        const email = localStorage.getItem('email');
        
        if (!purchaseId) {
          setError("Invalid payment session");
          setLoading(false);
          return;
        }
        let url = `interview-app/purchaseplan/complete/${purchaseId}`;
        if (email) {
          url += `?email=${email}`;
          localStorage.removeItem('email');
        }
        // Verify payment with backend
        const result = await getApiCall(url);
        
        if (result?.status && result?.result) {
          // Check payment status
          if (result.result.payment_status === 'paid' && result.result.status === 'complete') {
            // Get currency info based on country
            const country = result.result.country || 'US';
            const currencyInfo = getCurrencySymbol(country);
            
            // Set payment details from API response
            setPaymentDetails({
              planName: localStorage.getItem('currentPlanName') || 'Interview Plan',
              amount: (result.result.amount_total / 100).toFixed(2), // Convert cents to dollars
              timeAdded: result.result.purchase_minutes || '0',
              transactionId: purchaseId,
              country: result.result.country || 'Unknown',
              currencySymbol: currencyInfo.symbol,
              currencyCode: currencyInfo.code
            });
            
            // Clear purchase data from localStorage
            localStorage.removeItem('currentPurchaseId');
            localStorage.removeItem('currentPlanName');
            localStorage.removeItem('currentPlanPrice');
            localStorage.removeItem('currentPlanTime');
          } else {
            setError("Payment was not completed successfully");
          }
        } else {
          setError(result?.message || "Failed to verify payment");
        }
      } catch (err) {
        console.error("Error verifying payment:", err);
        setError("An error occurred while verifying your payment");
      } finally {
        setLoading(false);
      }
    };
    
    verifyPayment();
  }, []);
  
  const handleGoToHome = () => {
    navigate('/');
  };
  
  const handleGoToInterview = () => {
    navigate('/');
  };
  
  if (loading) {
    return (
      <>
        <Header />
        <div className="payment-result-container">
          <div className="payment-result-card loading">
            <SpinnerShow />
            <p>Verifying your payment...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <Header />
        <div className="payment-result-container">
          <div className="payment-result-card error">
            <h2>Payment Verification Failed</h2>
            <p>{error}</p>
            <button className="payment-result-button" onClick={handleGoToHome}>
              Return to Home
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  return (
    <>
      <Header />
      <div className="payment-result-container">
        <div className="payment-result-card success">
          <div className="success-icon">
            <CheckCircle />
          </div>
          <h1>Payment Successful!</h1>
          <p className="success-message">
            Thank you for your purchase. Your interview time has been added to your account.
          </p>
          
          {paymentDetails && (
            <div className="payment-details">
              <div className="payment-detail-item">
                <span>Plan:</span>
                <span>{paymentDetails.planName}</span>
              </div>
              <div className="payment-detail-item">
                <span>Amount Paid:</span>
                <span className="currency-amount">
                  {paymentDetails.currencySymbol}
                  {paymentDetails.amount}
                  {paymentDetails.currencyCode !== 'USD' && ` ${paymentDetails.currencyCode}`}
                </span>
              </div>
              <div className="payment-detail-item">
                <span>Time Added:</span>
                <span>{paymentDetails.timeAdded} minutes</span>
              </div>
              <div className="payment-detail-item">
                <span>Transaction ID:</span>
                <span>{paymentDetails.transactionId}</span>
              </div>
              {paymentDetails.country && (
                <div className="payment-detail-item">
                  <span>Billing Country:</span>
                  <span>{paymentDetails.country}</span>
                </div>
              )}
            </div>
          )}
          
          <div className="payment-result-actions">
            <button 
              className="payment-result-button primary" 
              onClick={handleGoToInterview}
            >
              Start Interviewing Now
            </button>
            <button 
              className="payment-result-button secondary" 
              onClick={handleGoToHome}
            >
              Return to Home
            </button>
          </div>
          
          <div className="payment-receipt-info">
            <p>A receipt has been sent to your email address.</p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default PaymentSuccessPage;




